import { IApiAxiosError } from "@/api";
import { ITopicMinimal, ITopicUpdate, updateTopic } from "@/api/topics";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { Controller, useForm } from "react-hook-form";

interface EditTopicDialogPayload {
    topic: ITopicMinimal;
}

interface EditTopicFormValues {
    name: string;
    description: string;
}

export const EditTopicDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<EditTopicDialogPayload>) => {
    const { topic } = payload;
    const queryClient = useQueryClient();

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors },
    } = useForm<EditTopicFormValues>({
        defaultValues: {
            name: topic.name,
            description: topic.description || "",
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: ITopicUpdate) => updateTopic(topic.id, data),
        onSuccess: () => {
            onClose();
            queryClient.invalidateQueries({
                queryKey: ["admin-topics"],
            });
            queryClient.invalidateQueries({
                queryKey: ["admin-topic-details", String(topic.id)],
            });
        },
        onError: (error) => {
            const axiosError = error as IApiAxiosError;
            setError("name", {
                type: "manual",
                message:
                    axiosError.response?.data.detail ||
                    "An error occurred while updating the topic.",
            });
        },
    });

    const onSubmit = (data: EditTopicFormValues) => {
        updateMutation.mutate({
            name: data.name.trim(),
            description: data.description.trim() || "",
        });
    };

    const handleClose = () => {
        reset({
            name: topic.name,
            description: topic.description || "",
        });
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Edit Topic"
            isLoading={updateMutation.isPending}
            actions={[
                {
                    label: "Save",
                    onClick: handleSubmit(onSubmit),
                    variant: "contained",
                    color: "primary",
                    disabled: updateMutation.isPending,
                },
            ]}
            footerText={
                updateMutation.error && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {(updateMutation.error as IApiAxiosError).response?.data
                            .detail ||
                            "An error occurred while updating the topic."}
                    </Typography>
                )
            }
        >
            <Stack spacing={2}>
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.trim() !== "" || "Name cannot be empty",
                    }}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Name"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            slotProps={{ htmlInput: { autoFocus: true } }}
                            required
                            sx={{ width: "100%" }}
                        />
                    )}
                />
                <Controller
                    name="description"
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Description"
                            helperText={`${field.value.length}/1000`}
                            multiline
                            minRows={3}
                            slotProps={{
                                htmlInput: { maxLength: 1000 },
                                formHelperText: {
                                    sx: {
                                        color:
                                            field.value.length >= 1000
                                                ? "error.main"
                                                : undefined,
                                    },
                                },
                            }}
                            sx={{ width: "100%" }}
                        />
                    )}
                />
            </Stack>
        </Modal>
    );
};
